import { Dispatch, ReactElement, SetStateAction, useContext, useEffect, useMemo, useRef } from "react";

import Grid from "@mui/material/Grid";
import { FormType, isBankDebitPaymentMethod, countryHasAch, PaymentMethod } from "@simplyk/common";
import { FieldValues, UseFormTrigger } from "react-hook-form";

import { BankConnectionGuidance } from "../PaymentElement/BankConnectionGuidance/BankConnectionGuidance";
import { ExpressCheckoutButtons } from "../PaymentElement/ExpressCheckoutButtons";

import PaymentErrorAlert from "../PaymentElement/PaymentErrorAlert";
import { type PaymentErrorContextType } from "../PaymentElement/PaymentErrorContext";
import { PaymentMethodOptions } from "../PaymentElement/PaymentMethodOptions";
import { PayPalDirectButton } from "../PaymentElement/PayPalDirectButton/PayPalDirectButton";
import { PreviewPaymentMethods } from "../PaymentElement/PreviewPaymentMethods";
import { SepaMandateConsent } from "../PaymentElement/SepaMandateConsent/SepaMandateConsent";
import { useAmplitudePaymentEventLog } from "../PaymentElement/useAmplitudePaymentEventLog";
import { useExpressCheckout } from "../PaymentElement/useExpressCheckout";
import { useSplitCoverFees } from "../PaymentElement/useSplitCoverFees";

import { type AmplitudeTicketingForPaymentLog, type StripePaymentComponentProps } from "./StripePaymentComponentProps";
import { useGenericPaymentSubmit } from "./useGenericPaymentSubmit";

import { HelpZeffyCoverFeesInfobox } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/HelpZeffyCoverFeesInfobox";
import { YoureHelpingZeffyCoverFeesInfobox } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/YoureHelpingZeffyCoverFeesInfobox";
import { ZeffySplitOrCoverFeesInfobox } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/ZeffySplitOrCoverFeesInfobox";
import { useBankDebitPaymentMethodLabel } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/helpers/useBankDebitPaymentMethodLabel";
import { DashboardUserAgreement } from "@/canvas/features/FormV2/blocks/payment/SummarySection/UserAgreement/DashboardUserAgreement";
import { useFormV2Context, useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { getDonationSelectionTracking } from "@/canvas/features/FormV2/helpers/getDonationSelectionTracking";
import { getFormAddOnAnalytics } from "@/canvas/features/FormV2/helpers/splitCartQuantities";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { ExpressCheckoutWallet, StripeContext } from "@/common/contexts/StripeContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DisplayGrid } from "@/components/Display/DisplayGrid";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DisplayWithInfoFormPreview } from "@/components/InfoForm/InfoForm";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitPaymentButton } from "@/components/SubmitPaymentButton/SubmitPaymentButton";

/** Stable fallback so `amplitudeEventLogModel` useMemo does not recompute when `amplitudeTicketingForPaymentLog` is omitted. */
const EMPTY_AMPLITUDE_TICKETING: AmplitudeTicketingForPaymentLog = {
  extraDonation: false,
  thermometerTarget: 0,
};

export type StripePaymentContentProps<T extends FieldValues> = Omit<
  StripePaymentComponentProps<T>,
  "selectedPaymentMethod" | "setSelectedPaymentMethod"
> & {
  /**
   * Form path: same instance as the outer `PaymentErrorContext` (avoid a second context consumer
   * under `StripeContext`/`Elements`); standalone/invoice: from `usePaymentErrorHandler` inside
   * `StripePaymentComponent`.
   */
  paymentErrorState: PaymentErrorContextType;
  hasPad: boolean;
  hasAch: boolean;
  stripePaymentMethods: PaymentMethod[];
  zeffyPaymentMethods: PaymentMethod[];
  expressCheckoutDisabled: boolean;
  isExpressCheckoutLoading: boolean;
  supportsExpressCheckout: boolean;
  displayExpressCheckout: boolean;
  handleSetSupportsExpressCheckout: (value: boolean) => void;
  prioritizeBankPayment: boolean;
  selectedPaymentMethod: PaymentMethod | undefined;
  setSelectedPaymentMethod: (method: PaymentMethod | undefined, complete?: boolean) => void;
  expressCheckoutIsSelected: boolean;
  defaultSelectedPaymentMethod: PaymentMethod | undefined;
  setExpressCheckoutWallet: Dispatch<SetStateAction<ExpressCheckoutWallet | undefined>>;
  isPreviewTemplateMode: boolean;
  tip: number;
  selectedPaymentMethodIsBank: boolean;
  selectedPaymentMethodIsCard: boolean;
  formattedFees: string | undefined;
  fees: number;
  updateFreeTipAmount: (amount: number) => void;
  allowedPaymentMethods: PaymentMethod[];
  isPaymentDisabled: boolean;
  isOnboarding: boolean;
  organizationCountry: import("@simplyk/common").OrganizationCountry;
  chequeDescription: string | null | undefined;
  offlinePaymentWording?: string | null;
  expressCheckoutAnalyticsContext?: import("../PaymentElement/useExpressCheckout").ExpressCheckoutAnalyticsContext;
};

export function StripePaymentContent<T extends FieldValues>({
  amount: _amountForElements,
  displayedFormSubtotalCents,
  currency,
  selectedTip,
  displayAddressQuestion,
  getTipChoice,
  handleSubmit,
  setValue,
  trigger,
  getValues,
  control,
  formStateErrors,
  formObject,
  organization,
  organizationCountry,
  formType,
  category,
  formData,
  formAmplitudeContext,
  commandId,
  isEmbed,
  isEmbedV2,
  postTransactionUrl,
  hasADate,
  generateETicket,
  isPaymentDisabled: isPaymentDisabledProp,
  isArchived,
  isDraft,
  paymentMode,
  tip,
  setIsSubmitting,
  setIsPadLoading,
  buildSubmitCommandFormInput,
  onPaymentSubmit,
  onPaymentSucceeded,
  onPayPalSubmit,
  tipConfig,
  shouldDisplaySubmitButton = true,
  submitPaymentButtonLabel,
  billingDetails,
  hasPad,
  hasAch,
  stripePaymentMethods,
  zeffyPaymentMethods,
  expressCheckoutDisabled,
  isExpressCheckoutLoading,
  displayExpressCheckout,
  handleSetSupportsExpressCheckout,
  prioritizeBankPayment,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  expressCheckoutIsSelected,
  defaultSelectedPaymentMethod,
  setExpressCheckoutWallet,
  isPreviewTemplateMode,
  selectedPaymentMethodIsBank,
  selectedPaymentMethodIsCard,
  formattedFees,
  fees,
  updateFreeTipAmount,
  allowedPaymentMethods,
  isOnboarding,
  chequeDescription,
  offlinePaymentWording,
  expressCheckoutAnalyticsContext,
  paymentErrorState: paymentErrorStateProp,
  amplitudeTicketingForPaymentLog,
  formQueryUtmForPaymentLog,
  isLiveFormEditorPreview: isLiveFormEditorPreviewProp = false,
}: StripePaymentContentProps<T>): ReactElement {
  const isCypressTest = typeof window !== "undefined" && Boolean((window as Window & { Cypress?: unknown }).Cypress);
  const { t } = useTranslate();
  const { paymentError, handlePaymentError, clearPaymentError, isBankConnectionGuidanceVisible } =
    paymentErrorStateProp;
  const { expressCheckoutWallet, stripeLoadError } = useContext(StripeContext);
  const { isAuction, collapseRecommendedBankPayment, isBankConnectionPending } = useContext(FrontendFormContext);
  const { locale, isoLocale } = useLocaleContext();
  const { impactChainUtmSourceEmail, source } = formQueryUtmForPaymentLog ?? {
    source: null,
    impactChainUtmSourceEmail: null,
  };
  const amplitudeTicketing = amplitudeTicketingForPaymentLog ?? EMPTY_AMPLITUDE_TICKETING;
  const organizationDisplayName = organization.name;
  const isLiveFormEditorPreview = isLiveFormEditorPreviewProp;

  const tipAmount = tipConfig ? (!tipConfig.shouldSendTipPercentage ? tipConfig.amount : 0) || 0 : tip;
  const tipPercentage = tipConfig ? (tipConfig.shouldSendTipPercentage ? tipConfig.percentage : 0) || 0 : 0;

  const { addOnRates, ticketsPurchased } = useTicketingFormV2ContextOrDefaults();
  const { itemsCount, addOnsCount } = getFormAddOnAnalytics(addOnRates, ticketsPurchased);

  const formV2Context = useFormV2Context();
  const amplitudeEventLogModel = useMemo(
    () => ({
      commandId: commandId ?? "",
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount: displayedFormSubtotalCents,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketing: {
        extraDonation: amplitudeTicketing.extraDonation,
        thermometerTarget: amplitudeTicketing.thermometerTarget,
      },
      itemsCount,
      addOnsCount,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet,
      formObject,
      getDonationSelection: () => getDonationSelectionTracking(formV2Context),
    }),
    [
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormSubtotalCents,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      amplitudeTicketing,
      itemsCount,
      addOnsCount,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet,
      formObject,
      formV2Context,
    ]
  );
  const { logDonorFormSubmitted } = useAmplitudePaymentEventLog({
    formStateErrors,
    formObject,
    model: amplitudeEventLogModel,
  });

  const commandFulfillment = formV2Context.type === FormType.Ticketing ? formV2Context.commandFulfillment : null;

  const { handleFormSubmit, createPaypalOrderGeneric, capturePaypalGeneric } = useGenericPaymentSubmit({
    handleSubmit,
    setValue,
    trigger: trigger as unknown as UseFormTrigger<DonationFormPaymentInput | TicketingPaymentInput>,
    getValues,
    selectedPaymentMethod,
    setIsSubmitting,
    setIsPadLoading,
    paymentMode: paymentMode ?? "payment",
    tipAmount,
    tipPercentage,
    organization,
    commandId,
    isArchived: Boolean(isArchived),
    isDraft: Boolean(isDraft),
    onPaymentSubmit,
    onPaymentSucceeded,
    onPayPalSubmit,
    formType,
    formCategory: category,
    formData,
    postTransactionUrl,
    hasADate,
    generateETicket,
    isEmbedV2: isEmbedV2 ?? false,
    logDonorFormSubmitted,
    buildSubmitCommandFormInput,
    paymentErrorHandler: { handlePaymentError, clearPaymentError },
    requireFulfillmentCheckout: commandFulfillment?.showFulfillmentOnPayment ?? false,
    canOfferSameAsBilling: displayAddressQuestion,
    offeredMethods: commandFulfillment?.offeredMethods ?? [],
  });

  const expressCheckout = useExpressCheckout({
    handleFormSubmit,
    setValue,
    trigger,
    control,
    formStateErrors,
    setExpressCheckoutWallet,
    organizationName: organizationDisplayName,
    setIsSubmitting,
    displayExpressCheckout,
    handleSetSupportsExpressCheckout,
    setSelectedPaymentMethod,
    selectedPaymentMethod,
    defaultSelectedPaymentMethod,
    isExpressCheckoutLoading,
    prioritizeBankPayment,
    analyticsContext: expressCheckoutAnalyticsContext,
    fulfillment: {
      isEnabled: commandFulfillment?.showFulfillmentOnPayment ?? false,
      shippingOnly: commandFulfillment?.shippingOnly ?? false,
      shippingRateLabel: t("ticketing", "fulfillment.shippingFee"),
    },
  });
  const { handleExpressCheckoutReady, handleExpressCheckoutClick, handleExpressCheckoutConfirm } = expressCheckout;

  const autoSelectedBankPaymentMethodRef = useRef<PaymentMethod | undefined>(undefined);
  const cypressHandlersRef = useRef({
    handleExpressCheckoutReady,
    handleExpressCheckoutClick,
    handleExpressCheckoutConfirm,
    setValue,
    trigger,
    selectedPaymentMethod,
  });
  cypressHandlersRef.current = {
    handleExpressCheckoutReady,
    handleExpressCheckoutClick,
    handleExpressCheckoutConfirm,
    setValue,
    trigger,
    selectedPaymentMethod,
  };
  if (isCypressTest) {
    (
      window as Window & { __EXPRESS_CHECKOUT_HANDLERS__?: typeof cypressHandlersRef.current }
    ).__EXPRESS_CHECKOUT_HANDLERS__ = cypressHandlersRef.current;
  }

  const splitFeesParams = useMemo(
    () => ({
      fees: tipConfig?.fees ?? fees,
      prioritizeBankPayment,
      selectedPaymentMethodIsCard,
      /** Match PaymentElement: raw tip from context; percentage tips still have a positive amount in `tip`. */
      tip,
      updateFreeTipAmount: tipConfig?.updateFreeTipAmount ?? updateFreeTipAmount,
      allowedPaymentMethods,
    }),
    [
      fees,
      prioritizeBankPayment,
      selectedPaymentMethodIsCard,
      tip,
      tipConfig,
      updateFreeTipAmount,
      allowedPaymentMethods,
    ]
  );

  const { handleCoverFees, handleSplitFees, onSplitCoverFeesClose, showSplitCoverFeesInfobox } =
    useSplitCoverFees(splitFeesParams);

  useEffect(() => {
    if (isPaymentDisabledProp) {
      handlePaymentError({
        rawErrorCode: "payment_disabled",
        rawErrorMessage: t("common", "paymentNotReadyBanner.description.disabled", {
          orgEmail: organization?.ownerEmail || "contact@zeffy.com",
        }),
        step: "form-validation",
        paymentMethod: undefined,
        isExpressCheckout: false,
        isKnownError: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentDisabledProp]);

  const showPayPalRow =
    shouldDisplaySubmitButton &&
    (selectedPaymentMethod === PaymentMethod.PayPal || selectedPaymentMethod === PaymentMethod.Venmo);

  const hasBankPaymentMethod = allowedPaymentMethods.some(isBankDebitPaymentMethod);
  const bankPaymentMethodLabel = useBankDebitPaymentMethodLabel(allowedPaymentMethods);
  const displayWalletsBelowPaymentMethods = countryHasAch(organizationCountry) && prioritizeBankPayment && hasAch;
  const renderPreviewButtons = isPreviewTemplateMode || isLiveFormEditorPreview;
  const hasWalletsBelow =
    displayWalletsBelowPaymentMethods &&
    (renderPreviewButtons ||
      (displayExpressCheckout &&
        (expressCheckout.availableWallets.applePay || expressCheckout.availableWallets.googlePay)));
  const showYoureHelpingZeffyCoverFeesInfobox = prioritizeBankPayment && selectedPaymentMethodIsBank;
  const showHelpZeffyCoverFeesInfobox =
    prioritizeBankPayment &&
    selectedPaymentMethodIsCard &&
    (tipConfig ? tipConfig.amount : tip) !== 0 &&
    hasBankPaymentMethod;

  const expressCheckoutButtons = (
    <ExpressCheckoutButtons
      expressCheckout={
        renderPreviewButtons
          ? { ...expressCheckout, availableWallets: { applePay: true, googlePay: true } }
          : expressCheckout
      }
      displayExpressCheckout={displayExpressCheckout}
      renderStripeButtons={!expressCheckoutDisabled && !isPreviewTemplateMode}
      renderPreviewButtons={renderPreviewButtons}
      belowPaymentMethods={displayWalletsBelowPaymentMethods}
      isAuction={isAuction}
      isOnboarding={isOnboarding}
      selectedPaymentMethod={selectedPaymentMethod}
      onSelectWallet={() => {
        clearPaymentError();
        setSelectedPaymentMethod(PaymentMethod.ApplePayOrGooglePay);
      }}
    />
  );

  return (
    <Grid container>
      <DisplayGrid when={Boolean(paymentError)} item xs={12} marginBottom={0}>
        <PaymentErrorAlert paymentError={paymentError} />
      </DisplayGrid>
      <DisplayWithInfoFormPreview
        content={t(formType === FormType.DonationForm ? "donationForm" : "ticketing", "preview.paymentMethod")}
      >
        {displayWalletsBelowPaymentMethods && selectedPaymentMethod !== PaymentMethod.Daf && (
          <Grid item xs={12} marginBottom={3}>
            <YoureHelpingZeffyCoverFeesInfobox
              organizationName={organizationDisplayName}
              bankPaymentMethodLabel={bankPaymentMethodLabel}
              recommendBankPayment
            />
          </Grid>
        )}

        {!displayWalletsBelowPaymentMethods && expressCheckoutButtons}

        {(!isExpressCheckoutLoading || stripeLoadError || isPreviewTemplateMode || displayWalletsBelowPaymentMethods) &&
          !isLiveFormEditorPreview && (
            <Grid item xs={12}>
              <PaymentMethodOptions
                offlinePaymentWording={offlinePaymentWording}
                hasPad={hasPad}
                hasAch={hasAch}
                hasWalletsBelow={hasWalletsBelow}
                stripePaymentMethods={stripePaymentMethods}
                zeffyPaymentMethods={zeffyPaymentMethods}
                organizationName={organizationDisplayName}
                organizationCountry={organizationCountry}
                prioritizeBankPayment={prioritizeBankPayment}
                collapseRecommendedBankPayment={collapseRecommendedBankPayment}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                selectedPaymentMethod={selectedPaymentMethod}
                chequeDescription={chequeDescription}
                displayExpressCheckout={displayExpressCheckout}
                expressCheckoutIsSelected={expressCheckoutIsSelected}
                onClearPaymentError={clearPaymentError}
                onPaymentError={handlePaymentError}
                autoSelectedBankPaymentMethodRef={autoSelectedBankPaymentMethodRef}
              />
            </Grid>
          )}

        {isBankConnectionGuidanceVisible && isBankConnectionPending && (
          <Grid item xs={12} marginTop={2}>
            <BankConnectionGuidance />
          </Grid>
        )}

        {isLiveFormEditorPreview && (
          <Grid item xs={12}>
            <PreviewPaymentMethods country={organizationCountry} />
          </Grid>
        )}

        {displayWalletsBelowPaymentMethods && expressCheckoutButtons}
      </DisplayWithInfoFormPreview>
      {selectedPaymentMethod === PaymentMethod.Sepa && (
        <Grid item xs={12} marginTop={3}>
          <SepaMandateConsent control={control} organizationName={organization.name} />
        </Grid>
      )}
      {billingDetails && (!isExpressCheckoutLoading || isPreviewTemplateMode || displayWalletsBelowPaymentMethods) && (
        <Grid item xs={12} marginTop={4}>
          {billingDetails}
        </Grid>
      )}

      {!displayWalletsBelowPaymentMethods && showYoureHelpingZeffyCoverFeesInfobox && (
        <Grid item xs={12} marginTop={4}>
          <YoureHelpingZeffyCoverFeesInfobox
            organizationName={organizationDisplayName}
            bankPaymentMethodLabel={bankPaymentMethodLabel}
          />
        </Grid>
      )}

      {!displayWalletsBelowPaymentMethods && showHelpZeffyCoverFeesInfobox && (
        <Grid item xs={12} marginTop={4}>
          <HelpZeffyCoverFeesInfobox
            organizationName={organizationDisplayName}
            bankPaymentMethodLabel={bankPaymentMethodLabel}
          />
        </Grid>
      )}

      {showSplitCoverFeesInfobox && formattedFees && (
        <Grid item xs={12} marginTop={4}>
          <ZeffySplitOrCoverFeesInfobox
            organizationName={organizationDisplayName}
            bankPaymentMethodLabel={bankPaymentMethodLabel}
            formattedFees={formattedFees}
            onSplitFeesClick={handleSplitFees}
            onCoverFeesClick={handleCoverFees}
            onDismiss={onSplitCoverFeesClose}
          />
        </Grid>
      )}

      {showPayPalRow && selectedPaymentMethod && (
        <Grid container item xs={12} marginTop={2}>
          <PayPalDirectButton
            selectedPaymentMethod={selectedPaymentMethod}
            createPaypalOrder={createPaypalOrderGeneric}
            capturePaypal={capturePaypalGeneric}
          />
        </Grid>
      )}

      {shouldDisplaySubmitButton && (
        <Grid container item xs={12} marginTop={4} marginBottom={2}>
          <SubmitPaymentButton handleFormSubmit={handleFormSubmit} submitButtonLabel={submitPaymentButtonLabel} />
          <Grid item xs={12} marginTop={1}>
            <DashboardUserAgreement />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
